import { normalizeKioskStationCount } from "./restaurantProfile";

const KIOSK_ANDROID_LAUNCHER_STORAGE_KEY = "mozzoapp:kiosk-android-launcher";
const KIOSK_WINDOWS_LAUNCHER_STORAGE_KEY = "mozzoapp:kiosk-windows-launcher";
const KIOSK_DEVICE_ID_STORAGE_KEY = "mozzoapp:kiosk-device-id";

const normalizeBranchId = (value) => String(value || "").trim();

export const normalizeKioskLauncherMode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  return normalized === "android" || normalized === "windows" ? normalized : "";
};

const resolveLauncherStorageKey = (launcherMode = "android") =>
  normalizeKioskLauncherMode(launcherMode) === "windows"
    ? KIOSK_WINDOWS_LAUNCHER_STORAGE_KEY
    : KIOSK_ANDROID_LAUNCHER_STORAGE_KEY;

const normalizeStationNumber = (value) => {
  const normalized = String(value || "").trim();

  if (!normalized) {
    return "";
  }

  return String(normalizeKioskStationCount(normalized));
};

const resolveBaseUrl = (baseUrl = "") => {
  if (baseUrl) {
    return String(baseUrl).trim().replace(/\/$/, "");
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }

  return "";
};

export const buildKioskLauncherPath = ({ branchId, stationNumber = "" } = {}) => {
  const normalizedBranchId = normalizeBranchId(branchId);
  const normalizedStationNumber = normalizeStationNumber(stationNumber);

  if (!normalizedBranchId) {
    return "";
  }

  if (normalizedStationNumber) {
    return `/totem/${normalizedBranchId}/${normalizedStationNumber}`;
  }

  return `/totem/${normalizedBranchId}`;
};

export const buildKioskAndroidLauncherUrl = ({
  baseUrl = "",
  branchId,
  stationNumber = "",
  setupMode = false,
} = {}) => {
  return buildKioskLauncherUrl({
    baseUrl,
    branchId,
    stationNumber,
    setupMode,
    launcherMode: "android",
  });
};

export const buildKioskWindowsLauncherUrl = ({
  baseUrl = "",
  branchId,
  stationNumber = "",
  setupMode = false,
} = {}) => {
  return buildKioskLauncherUrl({
    baseUrl,
    branchId,
    stationNumber,
    setupMode,
    launcherMode: "windows",
  });
};

export const buildKioskLauncherUrl = ({
  baseUrl = "",
  branchId,
  stationNumber = "",
  setupMode = false,
  launcherMode = "",
} = {}) => {
  const resolvedBaseUrl = resolveBaseUrl(baseUrl);
  const path = buildKioskLauncherPath({ branchId, stationNumber });
  const normalizedLauncherMode = normalizeKioskLauncherMode(launcherMode);

  if (!resolvedBaseUrl || !path) {
    return "";
  }

  const searchParams = new URLSearchParams();
  if (normalizedLauncherMode) {
    searchParams.set("launcher", normalizedLauncherMode);
  }

  if (!normalizeStationNumber(stationNumber) && setupMode) {
    searchParams.set("setup", "1");
  }

  const queryString = searchParams.toString();

  return `${resolvedBaseUrl}${path}${queryString ? `?${queryString}` : ""}`;
};

export const saveKioskLauncherConfig = ({
  branchId,
  stationNumber = "",
  launcherMode = "android",
} = {}) => {
  if (typeof window === "undefined") {
    return null;
  }

  const normalizedBranchId = normalizeBranchId(branchId);
  const normalizedStationNumber = normalizeStationNumber(stationNumber);

  if (!normalizedBranchId) {
    return null;
  }

  const payload = {
    branchId: normalizedBranchId,
    stationNumber: normalizedStationNumber,
    updatedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(resolveLauncherStorageKey(launcherMode), JSON.stringify(payload));

  return payload;
};

export const saveKioskAndroidLauncherConfig = (payload = {}) =>
  saveKioskLauncherConfig({ ...payload, launcherMode: "android" });

export const saveKioskWindowsLauncherConfig = (payload = {}) =>
  saveKioskLauncherConfig({ ...payload, launcherMode: "windows" });

export const loadKioskLauncherConfig = (launcherMode = "android") => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(resolveLauncherStorageKey(launcherMode));

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    const normalizedBranchId = normalizeBranchId(parsedValue?.branchId);
    const normalizedStationNumber = normalizeStationNumber(parsedValue?.stationNumber);

    if (!normalizedBranchId) {
      return null;
    }

    return {
      branchId: normalizedBranchId,
      stationNumber: normalizedStationNumber,
    };
  } catch (error) {
    console.warn("No pudimos leer la configuracion local del launcher.", error);
    return null;
  }
};

export const loadKioskAndroidLauncherConfig = () => loadKioskLauncherConfig("android");

export const loadKioskWindowsLauncherConfig = () => loadKioskLauncherConfig("windows");

export const clearKioskLauncherConfig = (launcherMode = "android") => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(resolveLauncherStorageKey(launcherMode));
};

export const clearKioskAndroidLauncherConfig = () =>
  clearKioskLauncherConfig("android");

export const clearKioskWindowsLauncherConfig = () =>
  clearKioskLauncherConfig("windows");

const buildKioskDeviceId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `kiosk-device-${window.crypto.randomUUID()}`;
  }

  return `kiosk-device-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

export const loadOrCreateKioskDeviceId = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const storedValue = String(
      window.localStorage.getItem(KIOSK_DEVICE_ID_STORAGE_KEY) || ""
    )
      .trim()
      .slice(0, 120);

    if (storedValue) {
      return storedValue;
    }

    const nextDeviceId = buildKioskDeviceId();
    window.localStorage.setItem(KIOSK_DEVICE_ID_STORAGE_KEY, nextDeviceId);
    return nextDeviceId;
  } catch (error) {
    return buildKioskDeviceId();
  }
};

export const isStandaloneAppDisplay = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone
  );
};
